import { useCallback, useState } from 'react'

export type SortDir = 'asc' | 'desc'
export interface SortCriterion<K extends string = string> {
  key: K
  dir: SortDir
}

/**
 * Multi-column table sort state, shared across the demand/product/submission
 * tables. Plain header click = single-column sort (toggle direction if it's
 * already the sole sort); Shift-click = add the column to the chain, or flip
 * its direction if already in it. Mirrors DemandList's original inline logic.
 */
export function useColumnSort<K extends string>(initial: SortCriterion<K>) {
  const [criteria, setCriteria] = useState<SortCriterion<K>[]>([initial])
  const handleSort = useCallback((key: K, additive: boolean) => {
    setCriteria((prev) => {
      const idx = prev.findIndex((c) => c.key === key)
      if (additive) {
        if (idx === -1) return [...prev, { key, dir: 'asc' }]
        const next = [...prev]
        next[idx] = { key, dir: prev[idx]?.dir === 'asc' ? 'desc' : 'asc' }
        return next
      }
      if (prev.length === 1 && idx === 0) {
        return [{ key, dir: prev[0]?.dir === 'asc' ? 'desc' : 'asc' }]
      }
      return [{ key, dir: 'asc' }]
    })
  }, [])
  return { criteria, handleSort }
}

/**
 * Sort rows by a criteria chain: the first column that distinguishes two rows
 * decides the order, so ties fall through to the next column. `compare` returns
 * an ascending comparison for a single key.
 */
export function sortByCriteria<T, K extends string>(
  rows: readonly T[],
  criteria: SortCriterion<K>[],
  compare: (a: T, b: T, key: K) => number,
): T[] {
  const sorted = [...rows]
  sorted.sort((a, b) => {
    for (const { key, dir } of criteria) {
      const cmp = compare(a, b, key)
      if (cmp !== 0) return dir === 'asc' ? cmp : -cmp
    }
    return 0
  })
  return sorted
}
